import { Skeleton } from "@herefor/design-system";
import { ONBOARDING_MESSAGES as COPY } from "@herefor/product-language";
import { useQuery } from "convex/react";
import { useState } from "react";

import { peopleQueries } from "../../convex/people";
import type { LivePlanResult } from "../../convex/plan";
import { OnboardingExperience } from "./OnboardingSurfaces";
import {
  onboardingArchetype,
  onboardingStorageKey,
  onboardingWasCompleted,
  rememberOnboardingCompletion,
} from "./onboardingData";

export interface FirstRunOnboardingProps {
  readonly forceReplay?: boolean;
  readonly result: LivePlanResult;
  readonly onStartHelper: () => void;
  readonly onStartOrganiser: () => void;
}

/** Live composition: membership-derived teaching plus local, per-person completion. */
export function FirstRunOnboarding({
  forceReplay = false,
  result,
  onStartHelper,
  onStartOrganiser,
}: FirstRunOnboardingProps) {
  const storageKey = onboardingStorageKey(result.publicPlanRef, result.viewerPersonId);
  const [completed, setCompleted] = useState(() =>
    onboardingWasCompleted(window.localStorage, storageKey),
  );
  const directory = useQuery(
    peopleQueries.list,
    result.canViewPeople ? { circleId: result.circleId } : "skip",
  );

  if (!forceReplay && completed) return null;
  if (result.canViewPeople && directory === undefined) {
    return <Skeleton label={COPY.eyebrow} lines={3} />;
  }

  const archetype = onboardingArchetype(result);
  const organiserName =
    directory?.people.find(
      (person) => person.role === "organiser" && person.accessState === "active",
    )?.chosenName ?? null;
  const complete = () => {
    rememberOnboardingCompletion(window.localStorage, storageKey);
    setCompleted(true);
  };

  return (
    <OnboardingExperience
      archetype={archetype}
      organiserName={organiserName}
      onPrimary={() => {
        complete();
        if (archetype === "organiser") onStartOrganiser();
        else if (archetype === "helper") onStartHelper();
      }}
      onSkip={complete}
      privacyHref={`/c/${result.publicPlanRef}/privacy`}
    />
  );
}
